// src/services/sharePointComsService.js
import { graphFetch } from "../auth/graphAuth";

const GRAPH_BASE = "https://graph.microsoft.com/v1.0";
const SP_HOSTNAME = "axillon.sharepoint.com";
const SP_SITE_PATH = "/sites/ComsandPolicies";
const DOC_LIBRARY_NAME = "Documents";

// Cache the drive ID after first resolution
let cachedDriveId = null;

/**
 * Resolve the SharePoint site ID from hostname + path.
 */
export async function getSiteId() {
  const url = `${GRAPH_BASE}/sites/${SP_HOSTNAME}:${SP_SITE_PATH}`;
  const res = await graphFetch(url);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`Failed to resolve site: ${res.status} ${err?.error?.message || ""}`);
  }
  const data = await res.json();
  return data.id;
}

/**
 * Get the drive ID for the "Shared Documents" library.
 */
export async function getDriveId() {
  if (cachedDriveId) return cachedDriveId;

  const siteId = await getSiteId();
  const url = `${GRAPH_BASE}/sites/${siteId}/drives`;
  const res = await graphFetch(url);
  if (!res.ok) {
    throw new Error(`Failed to list drives: ${res.status}`);
  }
  const data = await res.json();
  const drive = data.value.find((d) => d.name === DOC_LIBRARY_NAME);
  if (!drive) {
    throw new Error(
      `Document library "${DOC_LIBRARY_NAME}" not found. Available: ${data.value.map((d) => d.name).join(", ")}`
    );
  }
  cachedDriveId = drive.id;
  return drive.id;
}

/**
 * Resolve a top-level folder's item ID by name (e.g. "Coms" or "Policies").
 */
export async function getRootFolderId(folderName) {
  const driveId = await getDriveId();
  const url = `${GRAPH_BASE}/drives/${driveId}/root:/${encodeURIComponent(folderName)}`;
  const res = await graphFetch(url);
  if (!res.ok) {
    throw new Error(`Failed to resolve folder "${folderName}": ${res.status}`);
  }
  const data = await res.json();
  return data.id;
}

/**
 * List children of a folder.
 * If folderId is null/undefined, lists root of the library.
 */
export async function listItems(folderId) {
  const driveId = await getDriveId();
  const path = folderId
    ? `${GRAPH_BASE}/drives/${driveId}/items/${folderId}/children`
    : `${GRAPH_BASE}/drives/${driveId}/root/children`;

  const url = `${path}?$orderby=name&$select=id,name,size,lastModifiedDateTime,lastModifiedBy,folder,file,@microsoft.graph.downloadUrl`;
  const res = await graphFetch(url);
  if (!res.ok) {
    throw new Error(`Failed to list items: ${res.status}`);
  }
  const data = await res.json();
  return data.value;
}

/**
 * Download a file via the /content endpoint and trigger a browser download.
 */
export async function downloadFile(itemId, fileName) {
  const driveId = await getDriveId();
  const url = `${GRAPH_BASE}/drives/${driveId}/items/${itemId}/content`;
  const res = await graphFetch(url);
  if (!res.ok) {
    throw new Error(`Download failed: ${res.status}`);
  }
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

/**
 * Upload a small file (<4MB).
 */
export async function uploadSmallFile(parentFolderId, file) {
  const driveId = await getDriveId();
  const parentPath = parentFolderId
    ? `drives/${driveId}/items/${parentFolderId}`
    : `drives/${driveId}/root`;
  const url = `${GRAPH_BASE}/${parentPath}:/${encodeURIComponent(file.name)}:/content`;

  const res = await graphFetch(url, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`Upload failed: ${res.status} ${err?.error?.message || ""}`);
  }
  return res.json();
}

/**
 * Upload a large file (>4MB) using chunked upload session.
 */
export async function uploadLargeFile(parentFolderId, file, onProgress) {
  const driveId = await getDriveId();
  const parentPath = parentFolderId
    ? `drives/${driveId}/items/${parentFolderId}`
    : `drives/${driveId}/root`;
  const sessionUrl = `${GRAPH_BASE}/${parentPath}:/${encodeURIComponent(file.name)}:/createUploadSession`;

  const sessionRes = await graphFetch(sessionUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      item: { "@microsoft.graph.conflictBehavior": "rename" },
    }),
  });
  if (!sessionRes.ok) {
    throw new Error(`Failed to create upload session: ${sessionRes.status}`);
  }
  const session = await sessionRes.json();
  const uploadUrl = session.uploadUrl;

  const CHUNK_SIZE = 5 * 1024 * 1024;
  const totalSize = file.size;
  let offset = 0;

  while (offset < totalSize) {
    const end = Math.min(offset + CHUNK_SIZE, totalSize);
    const chunk = file.slice(offset, end);

    const chunkRes = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Length": `${end - offset}`,
        "Content-Range": `bytes ${offset}-${end - 1}/${totalSize}`,
      },
      body: chunk,
    });

    if (!chunkRes.ok && chunkRes.status !== 202) {
      throw new Error(`Chunk upload failed at offset ${offset}: ${chunkRes.status}`);
    }

    offset = end;
    if (onProgress) {
      onProgress(Math.round((offset / totalSize) * 100));
    }
  }
}

/**
 * Unified upload function.
 */
export async function uploadFile(parentFolderId, file, onProgress) {
  const FOUR_MB = 4 * 1024 * 1024;
  if (file.size <= FOUR_MB) {
    if (onProgress) onProgress(50);
    const result = await uploadSmallFile(parentFolderId, file);
    if (onProgress) onProgress(100);
    return result;
  }
  return uploadLargeFile(parentFolderId, file, onProgress);
}

/**
 * Delete a file or folder.
 */
export async function deleteItem(itemId) {
  const driveId = await getDriveId();
  const url = `${GRAPH_BASE}/drives/${driveId}/items/${itemId}`;
  const res = await graphFetch(url, { method: "DELETE" });
  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`Delete failed: ${res.status} ${err?.error?.message || ""}`);
  }
  return true;
}

/**
 * Move an item to a different folder.
 */
export async function moveItem(itemId, destinationFolderId) {
  const driveId = await getDriveId();
  const url = `${GRAPH_BASE}/drives/${driveId}/items/${itemId}`;
  const body = {
    parentReference: destinationFolderId
      ? { id: destinationFolderId }
      : { path: `/drives/${driveId}/root` },
  };
  const res = await graphFetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`Move failed: ${res.status} ${err?.error?.message || ""}`);
  }
  return res.json();
}

/**
 * List only folders in a given folder (for folder picker).
 */
export async function listFolders(folderId) {
  const driveId = await getDriveId();
  const path = folderId
    ? `${GRAPH_BASE}/drives/${driveId}/items/${folderId}/children`
    : `${GRAPH_BASE}/drives/${driveId}/root/children`;
  const url = `${path}?$filter=folder ne null&$orderby=name&$select=id,name,folder`;
  const res = await graphFetch(url);
  if (!res.ok) {
    throw new Error(`Failed to list folders: ${res.status}`);
  }
  const data = await res.json();
  return data.value;
}